import React from 'react';
import Error from './Error';
import PubSub from 'pubsub-js';
import GlobalConst from '../utils/GlobalConst';

/**
 * React Error Boundary to catch React component errors
 * Prevents the entire app from crashing and displays errors gracefully
 */
class ErrorBoundary extends React.Component {
	constructor(props) {
		super(props);
		this.state = { hasError: false, error: null, errorInfo: null };
	}

	static getDerivedStateFromError(_error) {
		// Update state so the next render will show the fallback UI
		return { hasError: true };
	}

	componentDidCatch(error, errorInfo) {
		// Log error details
		/*eslint-disable no-console */
		console.error('ErrorBoundary caught an error:', error, errorInfo);
		/*eslint-enable no-console */

		this.setState({
			error: error,
			errorInfo: errorInfo
		});

		// If it's an API error (has responseStatus), show it in a modal
		if (error && typeof error === 'object' && error.responseStatus) {
			const errorMessage = {
				xhrError: error,
				readableMessage: error.responseStatus === 504
					? 'Request Timeout'
					: error.responseStatus === 408
						? 'Request Timeout'
						: error.json?.message || `Error ${error.responseStatus}`
			};

			PubSub.publish(GlobalConst.SHOW_MODAL_MSG, {
				type: GlobalConst.MODAL_TYPE_ERROR,
				message: errorMessage,
				title: 'Error'
			});
		}
	}

	render() {
		if (this.state.hasError) {
			// Render custom error UI
			const error = this.state.error;
			
			// If it's an API error, use the Error component
			if (error && typeof error === 'object' && error.responseStatus) {
				return <Error error={{ xhrError: error }} />;
			}
			
			// Otherwise, show a generic error message
			return (
				<div className="rendered-error" style={{ padding: '20px' }}>
					<h3>Something went wrong</h3>
					<p>An error occurred while rendering this component.</p>
					{process.env.NODE_ENV === 'development' && this.state.errorInfo && (
						<details style={{ marginTop: '10px' }}>
							<summary>Error Details</summary>
							<pre style={{ whiteSpace: 'pre-wrap', fontSize: '12px' }}>
								{this.state.error && this.state.error.toString()}
								{this.state.errorInfo.componentStack}
							</pre>
						</details>
					)}
				</div>
			);
		}

		return this.props.children;
	}
}

export default ErrorBoundary;
